"use client";

import React, { forwardRef, useState, useRef, useEffect } from "react";
import { clsx } from "clsx";
import { FaCloudUploadAlt, FaFile, FaTimes } from "react-icons/fa";

interface FileUploadProps extends React.InputHTMLAttributes<HTMLInputElement> {
	onFilesChange?: (files: File[]) => void;
	/** When provided, the component is controlled: this list is shown and clearing it (e.g. after success) clears the UI */
	files?: File[];
	error?: string;
}

const FileUpload = forwardRef<HTMLInputElement, FileUploadProps>(
	({ className, onFilesChange, files: controlledFiles, error, ...props }, ref) => {
		const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
		const inputRef = useRef<HTMLInputElement>(null);

		const isControlled = controlledFiles !== undefined;
		const displayFiles = isControlled ? controlledFiles : selectedFiles;

		// When controlled and parent clears files (e.g. after success), clear the file input too
		useEffect(() => {
			if (isControlled && controlledFiles !== undefined && controlledFiles.length === 0 && inputRef.current) {
				inputRef.current.value = "";
			}
		}, [isControlled, controlledFiles?.length]);

		const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const newFiles = Array.from(e.target.files || []);
			if (isControlled && onFilesChange) {
				onFilesChange([...controlledFiles, ...newFiles]);
			} else {
				setSelectedFiles(newFiles);
				if (onFilesChange) onFilesChange(newFiles);
			}
			if (props.onChange) props.onChange(e);
		};

		const removeFile = (index: number) => {
			const newFiles = displayFiles.filter((_, i) => i !== index);
			if (isControlled && onFilesChange) {
				onFilesChange(newFiles);
			} else {
				setSelectedFiles(newFiles);
				if (onFilesChange) onFilesChange(newFiles);
			}
		};

		return (
			<div className={clsx("w-full", className)}>
				<label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500">
					<div className="flex flex-col items-center justify-center pt-5 pb-6">
						<FaCloudUploadAlt className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" />
						<p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
							<span className="font-semibold">Click to upload</span> or drag and
							drop
						</p>
						<p className="text-xs text-gray-500 dark:text-gray-400">
							PDF, DOC, DOCX (MAX. 10MB)
						</p>
					</div>
					<input
						type="file"
						className="hidden"
						onChange={handleFileChange}
						multiple
						ref={(el) => {
							(inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
							if (typeof ref === "function") ref(el);
							else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
						}}
						{...props}
					/>
				</label>

				{displayFiles.length > 0 && (
					<ul className="mt-4 space-y-2">
						{displayFiles.map((file, index) => (
							<li
								key={index}
								className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded shadow-sm border dark:border-gray-700"
							>
								<div className="flex items-center space-x-2 truncate">
									<FaFile className="text-primary" />
									<span className="text-sm truncate dark:text-gray-300">
										{file.name}
									</span>
									<span className="text-xs text-gray-500">
										({(file.size / 1024 / 1024).toFixed(2)} MB)
									</span>
								</div>
								<button
									type="button"
									onClick={() => removeFile(index)}
									className="text-red-500 hover:text-red-700"
								>
									<FaTimes />
								</button>
							</li>
						))}
					</ul>
				)}
				{error && <p className="text-red-500 text-sm mt-1">{error}</p>}
			</div>
		);
	},
);

FileUpload.displayName = "FileUpload";

export default FileUpload;
